/**
 * Session inactivity timeout: keep-alive heartbeat, warning modal, countdown, and the
 * single-tab logout election. Shared by web (eapp) and management (portal).
 *
 * Call initOITimeout() once per page from the app's deferred bundle, passing that app's
 * timings. Differences between the apps are expressed as config rather than as
 * `if (app === 'web')` branches, so the shared code stays one behaviour.
 *
 * DOM contract, supplied by the app layouts:
 *   #timeoutModal       warning modal
 *   #time-remains       countdown text, rendered as m:ss
 *   #closeTimeOutModal  "I'm Still Here" button, must carry data-bs-dismiss="modal"
 *
 * Depends on getContextPath() and oiCSRFToken from oi-common.js, which loads first.
 */

let oiTimeout;
let oiTimeoutInitialized = false;

function createOITimeout() {
  const settings = {
      app: 'web', //'web' | 'mgmnt' - diagnostics only
      warningMs: 720000, //idle time before the warning modal appears
      countdownMs: 180000, //countdown shown in the modal before logging out
      tickMs: 1000, //how often the countdown redraws
      extraExclusions: [] //context-relative paths that should not arm a timer
    };

    const tabId = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);

    let exclusions = new Set();
    let claimKey = '';
    let sessionTimerId = null;
    let countdownTimerId = null;
    let worker = null;
    let logoutAt = 0;

    const contextPath = () => getContextPath();

    const isExcluded = () => exclusions.has(location.pathname);

    //poll the server and tell it that we're here
    const heartbeat = () => {
      fetch(contextPath() + '/eapp/heartbeat/simple')
        .then((response) => response.json())
        .then((data) => doDebug(data))
        .catch((err) => console.error(err));
    };

    /* ------------------------------------------------------------------ logout */

    const goToLogoutSuccess = () => {
      window.location = contextPath() + '/eapp/logoutSuccess';
    };

    /**
     * Elect a single tab to POST the logout. The session and its CSRF token are shared
     * across every tab in the browser, so once one tab logs out the others are holding a
     * token the server has already replaced - posting it fails CSRF instead of logging out.
     * Returns false when another tab already claimed the logout.
     *
     * Must only be called from inside withLogoutLock: the read-then-write here is a
     * check-then-act that two tabs racing at the same instant could both pass, submitting
     * two competing /logout POSTs (the second hits an already-invalidated session).
     */
    const claimLogout = () => {
      let store;

      try {
        store = window.localStorage;
      } catch {
        return true; //storage blocked - fall back to posting from every tab
      }
      if (!store) {
        return true;
      }

      const now = Date.now();
      const claimedAt = Number.parseInt(store.getItem(claimKey)?.split('|')[0], 10);

      //A claim older than one full inactivity cycle must be stale: a second legitimate
      //logout needs a re-login plus another full cycle, so it can never be this recent.
      if (claimedAt && now - claimedAt < settings.warningMs + settings.countdownMs) {
        return false;
      }

      const claim = `${now}|${tabId}`;

      store.setItem(claimKey, claim);
      //last write wins, so only the tab whose claim survived proceeds
      return store.getItem(claimKey) === claim;
    };

    //Runs fn with cross-tab mutual exclusion on the claim key so claimLogout's
    //check-then-act can never interleave with another tab's. Falls back to running fn
    //unsynchronized where the Web Locks API is unavailable.
    const withLogoutLock = (fn) => {
      if (navigator.locks?.request) {
        navigator.locks.request(claimKey, fn);
      } else {
        fn();
      }
    };

    const logout = () => {
      stop();

      withLogoutLock(() => {
        if (!claimLogout()) {
          goToLogoutSuccess();
          return;
        }

        const form = document.createElement('form');
        const token = document.createElement('input');

        form.method = 'POST';
        form.action = contextPath() + '/logout';
        token.name = '_csrf';
        token.value = oiCSRFToken;
        form.appendChild(token);
        document.body.appendChild(form);
        form.submit();
      });
    };

    /* ------------------------------------------------------------- worker + timers */

    //A worker thread keeps time even when the tab is backgrounded and its timers are
    //throttled, so a minimised tab still reaches its deadline.
    const startWorker = () => {
      if (typeof Worker === 'undefined') {
        return;
      }
      worker = new Worker(contextPath() + '/static/common/js/timeoutWorker.js');
      worker.onmessage = () => logout();
      worker.postMessage({ totalMs: settings.warningMs + settings.countdownMs });
    };

    //Single teardown path. Restarting used to leave the old countdown and worker running,
    //so every "I'm Still Here" click stacked another timer chain on top of the last.
    const stop = () => {
      if (sessionTimerId) {
        clearTimeout(sessionTimerId);
        sessionTimerId = null;
      }
      if (countdownTimerId) {
        clearTimeout(countdownTimerId);
        countdownTimerId = null;
      }
      if (worker) {
        worker.terminate();
        worker = null;
      }
    };

    const renderRemaining = () => {
      const timeRemains = document.getElementById('time-remains');

      if (!timeRemains) {
        return;
      }
      const totalSecs = Math.max(0, Math.ceil((logoutAt - Date.now()) / 1000));
      const mins = Math.floor(totalSecs / 60);
      const secs = totalSecs % 60;

      timeRemains.textContent = `${mins}:${secs < 10 ? '0' + secs : secs}`;
    };

    //Driven off an absolute deadline, so a tab that was throttled or asleep simply finds
    //the deadline already passed instead of needing a separate catch-up check.
    const runCountdown = () => {
      if (Date.now() >= logoutAt) {
        logout();
        return;
      }
      renderRemaining();
      countdownTimerId = setTimeout(runCountdown, settings.tickMs);
    };

    const showWarning = () => {
      const modalEl = document.getElementById('timeoutModal');

      if (modalEl) {
        new bootstrap.Modal(modalEl, { backdrop: true, keyboard: true }).show();
      }
      runCountdown();
    };

    /**
     * Arm a fresh inactivity cycle, cancelling any cycle already in flight. Safe to call
     * repeatedly - this is also the "user is still here" entry point.
     */
    const restart = () => {
      stop();

      if (isExcluded()) {
        return;
      }
      logoutAt = Date.now() + settings.warningMs + settings.countdownMs;
      sessionTimerId = setTimeout(showWarning, settings.warningMs);
      startWorker();
    };

    /* ------------------------------------------------------------------ init */

    const init = (options = {}) => {
      Object.assign(settings, options);

      const ctx = contextPath();

      //Derived from the running context path, so /ehp, /pf, /ehpportal and /management all
      //resolve without each app hardcoding the other environments' prefixes.
      exclusions = new Set(
        [
          '/logout',
          '/eapp/login',
          '/eapp/logoutSuccess',
          '/eapp/timeout',
          ...settings.extraExclusions
        ].map((path) => ctx + path)
      );

      //localStorage is keyed by origin, not path, and both apps are served from the same
      //host - scope the claim to this app's context so they cannot suppress each other.
      claimKey = `oiTimeoutLogoutClaim:${ctx}`;

      //A storage event never fires in the tab that wrote the value, so this is how the
      //remaining tabs learn a logout happened. They follow immediately instead of waiting
      //for their own countdown, which may be minutes behind depending on when each tab
      //was opened.
      window.addEventListener('storage', (event) => {
        if (event.key === claimKey && event.newValue && !isExcluded()) {
          goToLogoutSuccess();
        }
      });

      //#closeTimeOutModal carries data-bs-dismiss="modal", so dismissing the modal by any
      //route - button, backdrop or Esc - lands here. Wiring the button separately as well
      //would restart the cycle twice per click.
      const modalEl = document.getElementById('timeoutModal');

      if (modalEl) {
        modalEl.addEventListener('hidden.bs.modal', () => {
          heartbeat();
          restart();
        });
      }

      restart();
    };

  return { init, restart, stop, logout, heartbeat };
}

function initOITimeout(options = {}) {
  // Lazily initialize the timeout module on first call
  if (!oiTimeoutInitialized) {
    oiTimeout = createOITimeout();
    oiTimeoutInitialized = true;
  }

  if (oiTimeout) {
    oiTimeout.init(options);
  }
}

//Legacy globals: management/.../groups/application/group.jsp calls both of these inline.
function doHeartBeat() {
  if (oiTimeout) {
    oiTimeout.heartbeat();
  }
}

function doTimeoutHandler() {
  if (oiTimeout) {
    oiTimeout.restart();
  }
}
